import { raise } from '@sparkdotfi/common-universal'
import { useChainId, useChains, useDisconnect } from 'wagmi'

import { TopbarProps } from '../components/topbar/Topbar'
import { useConnectedWalletInfo } from './use-connected-wallet-info/useConnectedWalletInfo'
import { useNavigationInfo } from './useNavigationInfo'
import { usePrefetchCanWalletBatchQuery } from './usePrefetchCanWalletBatchQuery'
import { usePrefetchSavingsGeneralStats } from './usePrefetchSavingsGeneralStats'
import { usePrefetchTokenApprovals } from './usePrefetchTokenApprovals'
import { useRewardsInfo } from './useRewardsInfo'
import { useSparkRewardsSummary } from './useSparkRewardsSummary'
import { tutorialDialogConfig } from '@/base-miniapp/tutorial-dialog/TutorialDialog'
import { getChainConfigEntry } from '@/config/chain'
import { useConnectModal } from '@/domain/hooks/wallet'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { useOpenDialog } from '@/domain/state/dialogs/hooks'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { useWatchWalletModeActions } from '@/domain/wallet/useWatchWalletModeActions'
import { selectNetworkDialogConfig } from '@/features/dialogs/select-network/SelectNetworkDialog'
import { watchWalletDialogConfig } from '@/features/dialogs/watch-wallet/WatchWalletDialog'
import { SupportedChain } from '@/features/topbar/types'
import { getBuildInfo } from '@/ui/utils/getBuildInfo'

interface UseTopbarParams {
  isMobileDisplay: boolean
}

export function useTopbar({ isMobileDisplay }: UseTopbarParams): TopbarProps {
  const currentChainId = useChainId()
  const chains = useChains()
  const { openConnectModal = () => {} } = useConnectModal()
  const { account: address, connector, walletMode } = useConnectedAddress({ throwOnNotConnected: false })
  const { disableWatchWalletMode } = useWatchWalletModeActions()
  const { isInSandbox, deleteSandbox, openSandboxModal } = useSandbox()
  const { disconnect } = useDisconnect()

  usePrefetchCanWalletBatchQuery()
  usePrefetchSavingsGeneralStats()
  usePrefetchTokenApprovals()

  const openDialog = useOpenDialog()

  const rewardsInfo = useRewardsInfo({
    chainId: currentChainId,
    address,
  })
  const sparkRewardsSummary = useSparkRewardsSummary()

  const navigationInfo = useNavigationInfo()

  const connectedWalletInfo = useConnectedWalletInfo({
    address,
    connector,
    isInSandbox,
    onDisconnect: disconnect,
    onExitWatchWalletMode: disableWatchWalletMode,
    isMobileDisplay,
    walletMode,
  })

  const supportedChains: SupportedChain[] = chains.map((chain) => {
    const { meta } = getChainConfigEntry(chain.id)
    return {
      id: chain.id,
      name: meta.name,
    }
  })

  const currentChain = supportedChains.find((chain) => chain.id === currentChainId) ?? {
    id: currentChainId,
    name: supportedChains[0]?.name ?? raise('No supported chains'),
  } // this fallback object is needed when we add new chains

  function handleSandboxModalOpen(): void {
    openSandboxModal({ asDrawer: isMobileDisplay })
  }

  function openSelectNetworkDialog(): void {
    openDialog(selectNetworkDialogConfig, {
      asDrawer: isMobileDisplay,
    })
  }

  function openWatchWalletDialog(): void {
    openDialog(watchWalletDialogConfig, { asDrawer: isMobileDisplay })
  }

  function handleWatchWalletModeClick(): void {
    if (walletMode === 'watch-wallet') {
      disableWatchWalletMode()
      return
    }
    openWatchWalletDialog()
  }

  return {
    menuInfo: {
      onSandboxModeClick: isInSandbox ? deleteSandbox : handleSandboxModalOpen,
      onWatchWalletModeClick: handleWatchWalletModeClick,
      isInSandbox,
      isInWatchWalletMode: walletMode === 'watch-wallet',
      buildInfo: getBuildInfo(),
      isMobileDisplay,
      rewardsInfo,
      sparkRewardsSummary,
      baseMiniAppOptions:
        import.meta.env.VITE_BASE_MINI_APP === '1'
          ? {
              openTutorialDialog: () => {
                openDialog(tutorialDialogConfig, {})
              },
            }
          : undefined,
    },
    navigationInfo,
    networkInfo: {
      currentChain,
      onSelectNetwork: openSelectNetworkDialog,
    },
    walletInfo: {
      onConnect: openConnectModal,
      connectedWalletInfo,
    },
    rewardsInfo,
    sparkRewardsSummary,
    isMobileDisplay,
  }
}
